import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService } from '@/chassis/data/services/base-form-service';
import { createNormalizer, FieldTransforms } from '@/chassis/data/services/normalizer-utils';
import { toPartnersPayload, type SupplierPartner } from './partners';

/** Socio do quadro societario: tipo/numero do documento, nome, cargo e percentual (cdcb-103). */
export type { SupplierPartner } from './partners';

export interface SupplierRecord {
    id: string;
    companyName: string;
    tradeName?: string;
    cnpj: string;
    stateRegistration?: string;
    municipalRegistration?: string;
    companyType: string;
    companySize: string;
    businessSector: string;
    mainCnae: string;
    address: string;
    addressNumber?: string;
    addressComplement?: string;
    addressDistrict?: string;
    postalCode: string;
    city: string;
    /** Codigo IBGE do municipio (preenchido pelo CEP). Vai no resultado exportado ao LCPE. */
    cityCode?: string;
    state: string;
    country: string;
    // Cadastro completo (Pato Branco / LCPE, 21/09/2026) — tudo opcional
    legalNatureCode?: string;
    mailingPostalCode?: string;
    mailingAddress?: string;
    mailingAddressNumber?: string;
    mailingAddressComplement?: string;
    mailingAddressDistrict?: string;
    mailingCity?: string;
    mailingState?: string;
    mailingCityCode?: string;
    mailingCountry?: string;
    partners?: SupplierPartner[];
    corporateRegistrationType?: string;
    corporateRegistrationNumber?: string;
    corporateRegistrationDate?: string;
    corporateValidFrom?: string;
    corporateValidUntil?: string;
    primaryPhone: string;
    corporateEmail: string;
    legalRepresentativeName: string;
    legalRepresentativeCpf: string;
    registrationStatus: string;
    supplierStatus?: string;
    // Art. 60 Lei 14.133/2021 — flags de desempate (ver SupplierRecord).
    isForeignCompany?: boolean;
    contractualPerformanceScore?: number;
    hasGenderEqualityProgram?: boolean;
    genderEqualityProgramStartedAt?: string;
    hasIntegrityProgram?: boolean;
    integrityProgramStartedAt?: string;
    investsInResearchAndDevelopment?: boolean;
    hasClimateMitigationProgram?: boolean;
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export interface SupplierFilters {
    searchTerm?: string;
    cnpj?: string;
    companyType?: string;
    companySize?: string;
    businessSector?: string;
    registrationStatus?: string;
    active?: string | boolean;
    page?: number;
    limit?: number;
}

export interface SupplierFormData {
    companyName: string;
    tradeName: string;
    cnpj: string;
    stateRegistration: string;
    municipalRegistration: string;
    companyType: string;
    companySize: string;
    businessSector: string;
    mainCnae: string;
    address: string;
    addressNumber: string;
    addressComplement: string;
    addressDistrict: string;
    postalCode: string;
    city: string;
    cityCode: string;
    state: string;
    country: string;
    legalNatureCode: string;
    mailingPostalCode: string;
    mailingAddress: string;
    mailingAddressNumber: string;
    mailingAddressComplement: string;
    mailingAddressDistrict: string;
    mailingCity: string;
    mailingState: string;
    mailingCityCode: string;
    mailingCountry: string;
    partners: SupplierPartner[];
    corporateRegistrationType: string;
    corporateRegistrationNumber: string;
    corporateRegistrationDate: string;
    corporateValidFrom: string;
    corporateValidUntil: string;
    primaryPhone: string;
    corporateEmail: string;
    legalRepresentativeName: string;
    legalRepresentativeCpf: string;
    registrationStatus: string;
    supplierStatus?: string;
    fiscalRegularityStatus?: string;
    socialRegularityStatus?: string;
    laborRegularityStatus?: string;
    isMeEppCoop?: boolean;
    // Art. 60 Lei 14.133/2021 — critérios de desempate (cascade resolver
    // em `managetiebreakingnegotiation.applicationservice.ts`).
    isForeignCompany?: boolean;                  // Art. 60 §1° II — brasileira prefere
    contractualPerformanceScore?: number;        // Art. 60 caput II — 0-100
    hasGenderEqualityProgram?: boolean;          // Art. 60 III — equidade de gênero
    genderEqualityProgramStartedAt?: string;
    hasIntegrityProgram?: boolean;               // Art. 60 IV — integridade / compliance
    integrityProgramStartedAt?: string;
    investsInResearchAndDevelopment?: boolean;   // Art. 60 §1° III — P&D nacional
    hasClimateMitigationProgram?: boolean;       // Art. 60 §1° IV — mitigação climática
    active: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export const resolveActiveFlag = (record: Partial<Pick<SupplierRecord, 'active' | 'active'>>) => {
    if (typeof record.active === 'boolean') return record.active;
    if (typeof record.active === 'boolean') return record.active;
    return true;
};

export const normalizeSupplierRecord = createNormalizer<SupplierRecord>({
    fields: [
        'id',
        'companyName',
        'tradeName',
        'cnpj',
        'stateRegistration',
        'municipalRegistration',
        'companyType',
        'companySize',
        'businessSector',
        'mainCnae',
        'address',
        'addressNumber',
        'addressComplement',
        'addressDistrict',
        'postalCode',
        'city',
        'cityCode',
        'state',
        'country',
        'legalNatureCode',
        'mailingPostalCode',
        'mailingAddress',
        'mailingAddressNumber',
        'mailingAddressComplement',
        'mailingAddressDistrict',
        'mailingCity',
        'mailingState',
        'mailingCityCode',
        'mailingCountry',
        { target: 'partners', transform: FieldTransforms.identity },
        'corporateRegistrationType',
        'corporateRegistrationNumber',
        { target: 'corporateRegistrationDate', transform: FieldTransforms.identity },
        { target: 'corporateValidFrom', transform: FieldTransforms.identity },
        { target: 'corporateValidUntil', transform: FieldTransforms.identity },
        'primaryPhone',
        'corporateEmail',
        'legalRepresentativeName',
        'legalRepresentativeCpf',
        'registrationStatus',
        'supplierStatus',
        // Art. 60 — flags de desempate (cascade Lei 14.133/2021)
        { target: 'isForeignCompany', transform: FieldTransforms.toBoolean },
        { target: 'contractualPerformanceScore', transform: FieldTransforms.toNumber },
        { target: 'hasGenderEqualityProgram', transform: FieldTransforms.toBoolean },
        { target: 'genderEqualityProgramStartedAt', transform: FieldTransforms.identity },
        { target: 'hasIntegrityProgram', transform: FieldTransforms.toBoolean },
        { target: 'integrityProgramStartedAt', transform: FieldTransforms.identity },
        { target: 'investsInResearchAndDevelopment', transform: FieldTransforms.toBoolean },
        { target: 'hasClimateMitigationProgram', transform: FieldTransforms.toBoolean },
        { target: 'active', source: 'active', transform: FieldTransforms.toBoolean },
        { target: 'active', source: 'active', transform: FieldTransforms.toBoolean },
        { target: 'createdAt', transform: FieldTransforms.identity },
        { target: 'updatedAt', transform: FieldTransforms.identity },
        'createdBy',
        'createdByName',
        'createdByUser',
        'updatedBy',
        'updatedByName',
        'updatedByUser',
    ],
    customize: (normalized) => {
        const resolvedActive = resolveActiveFlag(normalized);
        normalized.active = resolvedActive;
        normalized.active = resolvedActive;
    },
});

class SuppliersService extends BaseService<SupplierRecord, SupplierFormData, Partial<SupplierFormData>, SupplierFilters> {
    constructor() {
        super({
            basePath: '/suppliers',
            normalizeRecord: normalizeSupplierRecord,
        });
    }

    async toggleActive(id: string | number, active: boolean): Promise<SupplierRecord> {
        return this.update(id, { active } as Partial<SupplierFormData>);
    }
}

export const suppliersService = new SuppliersService();

const supplierFormDefaults: SupplierFormData = {
    companyName: '',
    tradeName: '',
    cnpj: '',
    stateRegistration: '',
    municipalRegistration: '',
    companyType: '',
    companySize: '',
    businessSector: '',
    mainCnae: '',
    address: '',
    addressNumber: '',
    addressComplement: '',
    addressDistrict: '',
    postalCode: '',
    city: '',
    cityCode: '',
    state: '',
    country: 'Brasil',
    legalNatureCode: '',
    mailingPostalCode: '',
    mailingAddress: '',
    mailingAddressNumber: '',
    mailingAddressComplement: '',
    mailingAddressDistrict: '',
    mailingCity: '',
    mailingState: '',
    mailingCityCode: '',
    mailingCountry: 'Brasil',
    partners: [],
    corporateRegistrationType: '',
    corporateRegistrationNumber: '',
    corporateRegistrationDate: '',
    corporateValidFrom: '',
    corporateValidUntil: '',
    primaryPhone: '',
    corporateEmail: '',
    legalRepresentativeName: '',
    legalRepresentativeCpf: '',
    registrationStatus: 'ativa',
    supplierStatus: 'REGISTERED',
    // Art. 60 — defaults neutros (campos opcionais)
    isForeignCompany: false,
    contractualPerformanceScore: undefined,
    hasGenderEqualityProgram: false,
    genderEqualityProgramStartedAt: undefined,
    hasIntegrityProgram: false,
    integrityProgramStartedAt: undefined,
    investsInResearchAndDevelopment: false,
    hasClimateMitigationProgram: false,
    active: true,
    createdAt: undefined,
    updatedAt: undefined,
    createdBy: undefined,
    createdByName: undefined,
    createdByUser: undefined,
    updatedBy: undefined,
    updatedByName: undefined,
    updatedByUser: undefined,
};

type SupplierStringField =
    | 'companyName'
    | 'tradeName'
    | 'cnpj'
    | 'stateRegistration'
    | 'municipalRegistration'
    | 'companyType'
    | 'companySize'
    | 'businessSector'
    | 'mainCnae'
    | 'address'
    | 'addressNumber'
    | 'addressComplement'
    | 'addressDistrict'
    | 'postalCode'
    | 'city'
    | 'cityCode'
    | 'state'
    | 'country'
    | 'legalNatureCode'
    | 'mailingPostalCode'
    | 'mailingAddress'
    | 'mailingAddressNumber'
    | 'mailingAddressComplement'
    | 'mailingAddressDistrict'
    | 'mailingCity'
    | 'mailingState'
    | 'mailingCityCode'
    | 'mailingCountry'
    | 'corporateRegistrationType'
    | 'corporateRegistrationNumber'
    | 'corporateRegistrationDate'
    | 'corporateValidFrom'
    | 'corporateValidUntil'
    | 'primaryPhone'
    | 'corporateEmail'
    | 'legalRepresentativeName'
    | 'legalRepresentativeCpf'
    | 'registrationStatus'
    | 'supplierStatus';

const stringFields: SupplierStringField[] = [
    'companyName',
    'tradeName',
    'cnpj',
    'stateRegistration',
    'municipalRegistration',
    'companyType',
    'companySize',
    'businessSector',
    'mainCnae',
    'address',
    'addressNumber',
    'addressComplement',
    'addressDistrict',
    'postalCode',
    'city',
    'cityCode',
    'state',
    'country',
    'legalNatureCode',
    'mailingPostalCode',
    'mailingAddress',
    'mailingAddressNumber',
    'mailingAddressComplement',
    'mailingAddressDistrict',
    'mailingCity',
    'mailingState',
    'mailingCityCode',
    'mailingCountry',
    'corporateRegistrationType',
    'corporateRegistrationNumber',
    'corporateRegistrationDate',
    'corporateValidFrom',
    'corporateValidUntil',
    'primaryPhone',
    'corporateEmail',
    'legalRepresentativeName',
    'legalRepresentativeCpf',
    'registrationStatus',
    'supplierStatus',
];

const sanitizeSupplierPayload = (formData: Partial<SupplierFormData>): Partial<SupplierFormData> => {
    const {
        createdAt,
        updatedAt,
        createdBy,
        createdByName,
        createdByUser,
        updatedBy,
        updatedByName,
        updatedByUser,
        ...submittable
    } = formData;

    const normalizedSubmittable = submittable as Partial<Record<keyof SupplierFormData, SupplierFormData[keyof SupplierFormData]>>;
    const payload: Partial<SupplierFormData> = {};

    stringFields.forEach((field) => {
        if (field in normalizedSubmittable) {
            const value = normalizedSubmittable[field];
            if (typeof value === 'string') {
                const trimmed = value.trim();
                payload[field] = trimmed as SupplierFormData[typeof field];
            }
        }
    });

    // Quadro societario: so linhas com nome, documento ou percentual; campos aparados,
    // CPF/CNPJ sem mascara e percentual numerico (o backend valida a soma de 100%).
    if (Array.isArray(normalizedSubmittable.partners)) {
        payload.partners = toPartnersPayload(normalizedSubmittable.partners as SupplierPartner[]);
    }

    // Garantir supplierStatus com default se não preenchido
    if (!payload.supplierStatus) {
        payload.supplierStatus = 'REGISTERED' as SupplierFormData['supplierStatus'];
    }

    if ('active' in normalizedSubmittable) {
        const resolvedActive = resolveActiveFlag({
            active: normalizedSubmittable.active as boolean | undefined,
        });
        payload.active = resolvedActive;
    }

    return payload;
};

/** Data ISO (ou vazio) -> valor de <input type="date"> (YYYY-MM-DD). */
const toDateInputValue = (value?: string | null): string => {
    if (!value) return '';
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
};

export const createSuppliersFormService = () =>
    new BaseFormService<SupplierRecord, SupplierFormData, SupplierFormData, Partial<SupplierFormData>, SupplierFilters>({
        basePath: '/suppliers',
        initialData: supplierFormDefaults,
        normalizeRecord: normalizeSupplierRecord,
        transformToForm: (record: SupplierRecord) => ({
            ...supplierFormDefaults,
            companyName: record.companyName || '',
            tradeName: record.tradeName || '',
            cnpj: record.cnpj || '',
            stateRegistration: record.stateRegistration || '',
            municipalRegistration: record.municipalRegistration || '',
            companyType: record.companyType || '',
            companySize: record.companySize || '',
            businessSector: record.businessSector || '',
            mainCnae: record.mainCnae || '',
            address: record.address || '',
            addressNumber: record.addressNumber || '',
            addressComplement: record.addressComplement || '',
            addressDistrict: record.addressDistrict || '',
            postalCode: record.postalCode || '',
            city: record.city || '',
            cityCode: record.cityCode || '',
            state: record.state || '',
            country: record.country || 'Brasil',
            legalNatureCode: record.legalNatureCode || '',
            mailingPostalCode: record.mailingPostalCode || '',
            mailingAddress: record.mailingAddress || '',
            mailingAddressNumber: record.mailingAddressNumber || '',
            mailingAddressComplement: record.mailingAddressComplement || '',
            mailingAddressDistrict: record.mailingAddressDistrict || '',
            mailingCity: record.mailingCity || '',
            mailingState: record.mailingState || '',
            mailingCityCode: record.mailingCityCode || '',
            mailingCountry: record.mailingCountry || 'Brasil',
            partners: Array.isArray(record.partners) ? record.partners : [],
            corporateRegistrationType: record.corporateRegistrationType || '',
            corporateRegistrationNumber: record.corporateRegistrationNumber || '',
            corporateRegistrationDate: toDateInputValue(record.corporateRegistrationDate),
            corporateValidFrom: toDateInputValue(record.corporateValidFrom),
            corporateValidUntil: toDateInputValue(record.corporateValidUntil),
            primaryPhone: record.primaryPhone || '',
            corporateEmail: record.corporateEmail || '',
            legalRepresentativeName: record.legalRepresentativeName || '',
            legalRepresentativeCpf: record.legalRepresentativeCpf || '',
            registrationStatus: record.registrationStatus || 'ativa',
            supplierStatus: record.supplierStatus || 'REGISTERED',
            active: resolveActiveFlag(record),
            createdAt: record.createdAt,
            updatedAt: record.updatedAt,
            createdBy: record.createdBy,
            createdByName: record.createdByName,
            createdByUser: record.createdByUser,
            updatedBy: record.updatedBy,
            updatedByName: record.updatedByName,
            updatedByUser: record.updatedByUser,
        }),
        transformForCreate: (formData: SupplierFormData) => sanitizeSupplierPayload(formData) as SupplierFormData,
        transformForUpdate: (formData: Partial<SupplierFormData>) => sanitizeSupplierPayload(formData),
    });

